/* eslint-disable */
import React, { Component } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import has from "lodash/has";
import isEmpty from "lodash/isEmpty";
import clone from "lodash/clone";

import Input from "../input/input";
import { generateId, isFunction, gatherClasses } from "../../lib/utils";
import createDetectElementResize from "../../lib/detectElementResize";
import TreeNode from "./treeNode";
import CustomNode from "./customNode";
import NodeShape from "./nodeShape";
import {
  excludeCustomFromChildren,
  optimisticToggleCheck,
  getOptimisticCheckState
} from "./optimisticToggleUtils";

import "./tree.scss";

/** Prefix is used to create Object key. Because if the Node value is numeric then the insertion order is not kept in the Object. Which makes Tree order unexpected. */
export const PREFIX = "oui-";

class Tree extends Component {
  constructor(props) {
    super(props);
    this.validateProps(props);
    this.state = {
      numberOfNodesToRender: props.numberOfNodesToRender,
      scrollTop: 0,
      startNodeIndex: 0,
      endNodeIndex: props.numberOfNodesToRender - 1,
      selectedAll: false
    };

    this.id = generateId("tree-");
    this.nodes = {};
    this.flattenNodes(props.nodes, props.showHiddenNodes, props.expanded);
    this.unSerializeLists({
      checked: props.checked,
      expanded: props.expanded,
      loading: props.loading
    });

    this.selectAllInputRef = React.createRef();
    this.treeContainer = React.createRef();
    this.detectElementResize = null;
  }

  componentDidMount = () => {
    const { allowSelectAll, checkable, checked } = this.props;
    const treeContainer = this.treeContainer.current,
      numberOfNodesToRender =
        Math.floor(treeContainer.clientHeight / this.props.nodeHeight) + 2,
      startNodeIndex = 0,
      endNodeIndex = startNodeIndex + numberOfNodesToRender - 1;
    this.setState({
      numberOfNodesToRender,
      startNodeIndex,
      endNodeIndex
    });

    // Adding resize listener on the tree's parent node
    const treeParent = treeContainer.parentNode;
    if (treeParent) {
      this.detectElementResize = createDetectElementResize();
      this.detectElementResize.addResizeListener(
        treeParent,
        this.recomputeTreeHeight
      );
    }

    // For safe side, check if allow select all and handle it on didmount
    if (allowSelectAll && checkable) {
      this.handleSelectAllOnPropChanges(checked, this.props);
      // this.bindIESpecificEventToSelectAll();
    }
  };

  componentWillReceiveProps({
    nodes,
    checked,
    expanded,
    loading,
    showHiddenNodes,
    allowSelectAll,
    ...props
  }) {
    this.validateProps(props);
    this.nodes = {};
    this.flattenNodes(nodes, showHiddenNodes, expanded);
    this.unSerializeLists({ checked, expanded, loading });

    // For safe side, check if allow select all and handle it on receiveprops
    if (allowSelectAll && props.checkable) {
      this.handleSelectAllOnPropChanges(checked, props);
    }
  }

  componentWillUnmount() {
    const treeParent = this.treeContainer.current.parentNode;
    if (this.detectElementResize && treeParent) {
      this.detectElementResize.removeResizeListener(
        treeParent,
        this.recomputeTreeHeight
      );
    }
  }

  bindIESpecificEventToSelectAll() {
    // The first time you click on a checkbox that has its indeterminate set to true will
    // simply change it to false in IE and the checkbox will show its real state or I would
    // say it will not trigger change. To fix this, attaching one time event handler
    if (
      window.navigator.userAgent.indexOf("Trident") >= 0 &&
      this.selectAllInputRef.current
    ) {
      this.selectAllInputRef.current.onclick = event => {
        if (!event.target.checked) {
          event.target.checked = true;
          const evt = document.createEvent("HTMLEvents");
          evt.initEvent("change", false, true);
          event.target.dispatchEvent(evt);
        }
      };
    }
  }

  getOnlyPureNodes() {
    const nodes = {};

    Object.keys(this.nodes).forEach(key => {
      const node = this.nodes[key];

      if (!this.isCustomNode(node)) {
        nodes[key] = node;
      }
    });

    return nodes;
  }

  getOnlyLeafNodes() {
    const nodes = this.nodes;
    const leafs = {};

    Object.keys(nodes).forEach(key => {
      const node = nodes[key];

      if (
        !this.isCustomNode(node) &&
        (!node.children || node.children.length === 0)
      ) {
        leafs[key] = node;
      }
    });

    return leafs;
  }

  getOnlyRootNodes() {
    const nodes = this.nodes;
    const roots = {};

    Object.keys(nodes).forEach(key => {
      const node = nodes[key];

      if (!this.isCustomNode(node) && node.parent === "root") {
        roots[key] = node;
      }
    });

    return roots;
  }

  checkIfAllNodesPresentInChecked(nodes, checked = []) {
    const { uniqueDataKey } = this.props;
    const keys = Object.keys(nodes);

    for (const key of keys) {
      const node = nodes[key];
      const value = node[uniqueDataKey];
      if (!checked.includes(value)) {
        return false;
      }
    }

    return true;
  }

  handleSelectAllOnPropChanges(checked, props) {
    const { optimisticToggle, returnOnlyLeafNodes } = props;
    const selectAllInput = this.selectAllInputRef.current;
    let allNodes = this.getOnlyPureNodes();

    if (returnOnlyLeafNodes) {
      allNodes = this.getOnlyLeafNodes();
    }

    if (optimisticToggle) {
      allNodes = this.getOnlyRootNodes();
    }

    const totalNodes = Object.keys(allNodes).length;
    const totalChecked = checked.length;

    if (totalChecked === 0) {
      selectAllInput.indeterminate = false;
      this.setState({
        selectedAll: false
      });
    } else {
      selectAllInput.indeterminate = !(
        totalNodes === totalChecked &&
        this.checkIfAllNodesPresentInChecked(allNodes, checked)
      );
      this.setState({
        selectedAll:
          totalNodes === totalChecked &&
          this.checkIfAllNodesPresentInChecked(allNodes, checked)
      });
    }
  }

  validateProps(props) {
    const { optimisticToggle, noCascade, checkable } = props || this.props;

    if (optimisticToggle && noCascade) {
      throw new Error(
        "optimisticToggle and noCascade cannot be used together."
      );
    } else if (optimisticToggle && !checkable) {
      throw new Error(
        "optimisticToggle can only be used with checkable nodes."
      );
    }
  }

  recomputeTreeHeight = _ => {
    const treeContainer = this.treeContainer.current,
      numberOfNodesToRender =
        Math.floor(
          treeContainer.parentElement.clientHeight / this.props.nodeHeight
        ) + 2,
      scrollTop = treeContainer.parentElement.scrollTop,
      startNodePosition = Math.ceil(scrollTop / this.props.nodeHeight),
      startNodeIndex =
        startNodePosition === 0 ? startNodePosition : startNodePosition - 1,
      endNodeIndex = startNodeIndex + numberOfNodesToRender - 1;

    this.setState({
      numberOfNodesToRender,
      startNodeIndex,
      endNodeIndex
    });
  };

  isCustomNode = node => {
    return isFunction(node.renderComponent);
  };

  flattenNodes = (
    nodes,
    showHiddenNodes,
    expanded,
    parentNodeValue = "root",
    level = 0
  ) => {
    if (!Array.isArray(nodes) || nodes.length === 0) {
      return;
    }
    const identifier = this.props.uniqueDataKey;
    nodes.forEach(node => {
      const nodeValue =
        this.isCustomNode(node) && !node[identifier]
          ? `${parentNodeValue}-custom-${level}`
          : node[identifier];
      const key = PREFIX + nodeValue;
      this.nodes[key] = {};
      this.nodes[key]["parent"] = parentNodeValue;
      this.nodes[key]["level"] = level;
      this.nodes[key]["value"] = nodeValue;

      // Remove the children from original node and attach it to the nodes mapping for further usage
      let clonedNode = clone(node);
      let childrenPropName = this.props.childrenProp;
      childrenPropName = isEmpty(this.props.childrenLevelMap)
        ? this.props.childrenProp
        : this.props.childrenLevelMap[level];
      clonedNode.childrenCount =
        has(node, childrenPropName) && Array.isArray(node[childrenPropName])
          ? node[childrenPropName].length
          : 0;
      delete clonedNode[childrenPropName];
      this.nodes[key]["originalNode"] = clonedNode;

      if (
        this.props.isStaticData &&
        clonedNode.childrenCount === 0 &&
        !node.hasOwnProperty("isLeaf")
      ) {
        node["isLeaf"] = true;
        node[childrenPropName] = [];
      }
      // Copying each key of the node
      for (let nd in node) {
        this.nodes[key][nd] = node[nd];
      }
      let childrenNodes = [];
      if (
        !isEmpty(this.props.childrenLevelMap) &&
        node.hasOwnProperty(this.props.childrenLevelMap[level])
      ) {
        childrenNodes = node[this.props.childrenLevelMap[level]];
      } else {
        childrenNodes = node[this.props.childrenProp];
      }
      this.flattenNodes(
        childrenNodes,
        showHiddenNodes,
        expanded,
        key,
        level + 1
      );
    });
  };

  unSerializeLists = lists => {
    // Reset values to false
    Object.keys(this.nodes).forEach(value => {
      Object.keys(lists).forEach(listKey => {
        const node = this.nodes[value];
        if (node && !this.isCustomNode(node)) {
          this.nodes[value][listKey] = false;
        }
      });
    });

    // Un-serialize values and set their nodes to true
    Object.keys(lists).forEach(listKey => {
      lists[listKey].forEach(value => {
        const targetNode = this.nodes[PREFIX + value];
        if (!targetNode) {
          console.error(`Could not fetch node ${value} for ${listKey}`);
        } else if (!this.isCustomNode(targetNode)) {
          targetNode[listKey] = true;
        }
      });
    });
  };

  serializeList = (key, onlyLeafs = false) => {
    const list = [];
    Object.keys(this.nodes).forEach(value => {
      if (this.nodes[value][key]) {
        const node = this.nodes[value];
        const children = node.children;
        const nodeValue = node[this.props.uniqueDataKey];

        // We will serialize only if it is a valid non-custom node
        // and has zero children if onlyLeafs is true or always if
        // onlyLeafs is false
        if (
          node &&
          !this.isCustomNode(node) &&
          (!onlyLeafs || (onlyLeafs && (!children || children.length === 0)))
        ) {
          list.push(nodeValue);
        }
      }
    });
    return list;
  };

  isEveryChildChecked = node => {
    const childrenPropName = this.getChildrenPropName(node);
    const children = excludeCustomFromChildren(node[childrenPropName]);
    return children.every(child => {
      if (!this.isChildrenEmpty(child)) {
        return this.isEveryChildChecked(child);
      }
      return this.nodes[PREFIX + child[this.props.uniqueDataKey]].checked;
    });
  };

  isSomeChildChecked = node => {
    const childrenPropName = this.getChildrenPropName(node);
    const children = excludeCustomFromChildren(node[childrenPropName]);
    return children.some(child => {
      if (!this.isChildrenEmpty(child)) {
        return this.isSomeChildChecked(child);
      }
      return (
        this.nodes[PREFIX + child[this.props.uniqueDataKey]].checked ||
        this.nodes[PREFIX + child[this.props.uniqueDataKey]].halfChecked
      );
    });
  };

  isChildrenEmpty = node => {
    const childrenPropName = this.getChildrenPropName(node);
    const children = excludeCustomFromChildren(node[childrenPropName]);
    return (
      children === null || (Array.isArray(children) && children.length <= 0)
    );
  };

  getCheckState = (node, noCascade) => {
    if (!this.props.checkable) {
      return this.props.checked.includes(node[this.props.uniqueDataKey])
        ? 1
        : 0;
    }

    if (noCascade) {
      return node.checked ? 1 : 0;
    }

    // If halfChecked key is true and there are no children, return 2 irrespective the number of children
    if (this.isChildrenEmpty(node) && node.halfChecked) {
      return 2;
    }
    if (this.isChildrenEmpty(node)) {
      return node.checked ? 1 : 0;
    }
    if (this.isEveryChildChecked(node)) {
      return 1; //check
    }
    if (this.isSomeChildChecked(node)) {
      return 2; //partial
    }
    return 0;
  };

  getLoadingState = node => {
    if (node.loading) {
      return true;
    }
    return false;
  };

  updateParentNodeCheckedState = node => {
    //check if all children are checked then check parent as well
    if (!node.value) {
      return;
    }
    const parentValue = this.nodes[PREFIX + node.value]
      ? this.nodes[PREFIX + node.value].parent
      : "";
    const parentNode = this.nodes[parentValue] || {};
    const childrenPropName = this.getChildrenPropName(parentNode);
    const children = parentNode[childrenPropName];
    if (children && children.length > 0) {
      this.toggleNode(
        "checked",
        parentNode,
        this.isEveryChildChecked(parentNode)
      );
      this.updateParentNodeCheckedState(parentNode);
    }
  };

  toggleChecked = (node, isChecked, noCascade = false) => {
    if (this.isChildrenEmpty(node)) {
      // Set the check status of a leaf node or an uncoupled parent if the node is not disabled
      if (!node[this.props.disabledProp]) {
        this.toggleNode("checked", node, isChecked);
      }
    } else {
      this.toggleNode("checked", node, isChecked);

      // If noCascade is false, that means we will maintain the cascade
      // heirarchy and check all of its children (excluding custom though)
      if (!noCascade) {
        const childrenPropName = this.getChildrenPropName(node);
        excludeCustomFromChildren(node[childrenPropName]).forEach(child => {
          this.toggleChecked(child, isChecked, noCascade);
        });
      }
    }

    // Irrespective of children count, if noCascade is false, then we will
    // update checked status of all of its parent nodes.
    if (!noCascade) {
      this.updateParentNodeCheckedState(node);
    }
  };

  toggleNode = (key, node, toggleValue) => {
    let targetNode = has(node, "value")
      ? this.nodes[PREFIX + node.value]
      : has(node, this.props.uniqueDataKey)
      ? this.nodes[PREFIX + node[this.props.uniqueDataKey]]
      : null;
    targetNode[key] = toggleValue;
  };

  getUnPrefixedNodes = _ => {
    let obj = {};
    for (const key in this.nodes) {
      const newKey = key.replace(PREFIX, "");
      const node = clone(this.nodes[key]);
      if (!this.isCustomNode(node)) {
        if (node.hasOwnProperty("parent")) {
          node.parent = node.parent.replace(PREFIX, "");
        }
        obj[newKey] = node;
      }
    }
    return obj;
  };

  onCheck = (node, originalNode) => {
    const { noCascade, onCheck, optimisticToggle } = this.props;

    if (optimisticToggle) {
      optimisticToggleCheck.call(this, node, node.checked);
    } else {
      this.toggleChecked(node, node.checked, noCascade);
    }

    // The last parameter in onCheck would be onlyLeaf nodes.
    if (this.props.checkable) {
      const checkedNodes = this.serializeList(
        "checked",
        this.props.returnOnlyLeafNodes
      );
      onCheck(checkedNodes, originalNode, this.getUnPrefixedNodes());
    } else {
      onCheck(
        [originalNode[this.props.uniqueDataKey]],
        originalNode,
        this.getUnPrefixedNodes()
      );
    }
  };

  onExpand = (node, originalNode) => {
    const { onExpand } = this.props;
    this.toggleNode("expanded", node, node.expanded);
    this.toggleNode("loading", node, node.expanded);
    onExpand(
      this.serializeList("expanded"),
      this.serializeList("loading"),
      node,
      originalNode
    );
  };

  onSelectAll = () => {
    const {
      onCheck,
      optimisticToggle,
      returnOnlyLeafNodes,
      uniqueDataKey
    } = this.props;
    const { selectedAll } = this.state;
    let nodesToReturn = this.getOnlyPureNodes();

    if (returnOnlyLeafNodes) {
      nodesToReturn = this.getOnlyLeafNodes();
    }

    if (optimisticToggle) {
      nodesToReturn = this.getOnlyRootNodes();
    }

    if (selectedAll) {
      onCheck([], undefined, this.getUnPrefixedNodes());
    } else {
      const checked = [];

      Object.keys(nodesToReturn).forEach(key => {
        const node = this.nodes[key];
        const nodeValue = node[uniqueDataKey];
        checked.push(nodeValue);
      });

      onCheck(checked, undefined, this.getUnPrefixedNodes());
    }
  };

  renderTreeNodes = nodes => {
    const {
      showHiddenNodes,
      expandDisabled,
      noCascade,
      optimisticToggle,
      showNodeIcon,
      nodeHeight
    } = this.props;
    let checkable = this.props.checkable;
    const treeNodes = nodes.map((node, index) => {
      const key = `${node.value}`;

      if (isFunction(node.renderComponent)) {
        return <CustomNode node={node} index={index} key={key} />;
      }

      const checked = optimisticToggle
        ? getOptimisticCheckState.call(this, node)
        : this.getCheckState(node, noCascade);
      const loading = this.getLoadingState(node);
      let nodeCheckable = checkable;
      // We not use checkable property for nodes except the 'inSync Share' node inside selective sync listing.
      // Backend sends 'checkable' as false for this node.
      if (node.hasOwnProperty(this.props.checkableProp)) {
        nodeCheckable = node[this.props.checkableProp];
      }
      return (
        <TreeNode
          tabIndex={index == 0 ? 0 : 1}
          checkable={nodeCheckable}
          showHiddenNodes={showHiddenNodes}
          isLeaf={node.isLeaf || false}
          key={key}
          checked={checked}
          className={node.className}
          disabled={node[this.props.disabledProp] || this.props.disabled}
          hidden={node[this.props.hiddenProp]}
          loading={loading}
          expandDisabled={expandDisabled}
          expanded={node.expanded}
          tooltipText={node[this.props.tooltipProp]}
          checkboxTooltipText={node[this.props.checkboxTooltipProp]}
          getCheckboxTooltip={this.props.getCheckboxTooltip}
          label={node[this.props.labelProp]}
          rawChildren={this.getRawChildren(node)}
          showNodeIcon={showNodeIcon}
          treeId={this.id}
          value={node.value}
          onCheck={this.onCheck}
          onExpand={this.onExpand}
          level={node.level}
          modified={node.modified}
          nodeHeight={nodeHeight}
          childrenPropName={this.getChildrenPropName(node)}
          renderIcon={this.props.renderIcon}
          renderLabel={this.props.renderLabel}
          renderSubLabel={this.props.renderSubLabel}
          renderNodeAction={this.props.renderNodeAction}
          originalNode={node.originalNode}
        />
      );
    });
    return treeNodes;
  };

  getChildrenPropName = node => {
    let childrenPropName = this.props.childrenProp;
    let targetNode = has(node, this.props.uniqueDataKey)
      ? this.nodes[PREFIX + node[this.props.uniqueDataKey]]
      : this.nodes[PREFIX + node.value];
    if (!targetNode) {
      return childrenPropName;
    }
    childrenPropName = isEmpty(this.props.childrenLevelMap)
      ? this.props.childrenProp
      : this.props.childrenLevelMap[targetNode.level];
    return childrenPropName;
  };

  getRawChildren = node => {
    return node[this.getChildrenPropName(node)];
  };

  getNodesToRender = (startNodeIndex, endNodeIndex, nodesArray) => {
    let nodesToRender = [];
    for (let i = 0; i < nodesArray.length; i++) {
      if (
        startNodeIndex - 2 <= nodesArray[i].index &&
        endNodeIndex >= nodesArray[i].index
      ) {
        nodesToRender.push(nodesArray[i]);
      }
    }
    nodesToRender.sort(function (a, b) {
      return parseFloat(a.index) - parseFloat(b.index);
    });
    return nodesToRender;
  };

  onScroll = () => {
    if (Object.keys(this.nodes).length <= this.state.numberOfNodesToRender) {
      return;
    }
    const scrollTop = this.treeContainer.current.scrollTop,
      startNodePosition = Math.ceil(scrollTop / this.props.nodeHeight),
      startNodeIndex =
        startNodePosition === 0 ? startNodePosition : startNodePosition - 1,
      endNodeIndex = startNodeIndex + this.state.numberOfNodesToRender - 1;
    this.setState({
      scrollTop,
      startNodeIndex,
      endNodeIndex
    });
  };

  isAnyParentCollapsed = (nodes, node) => {
    // If the parent's value is root it means there is no parent to this node.
    // We need to show the root node irrespective of the fact that it's expanded or collapsed.
    if (node.parent === "root") {
      return false;
    }
    if (nodes[node.parent] && !nodes[node.parent].expanded) {
      return true;
    } else {
      return this.isAnyParentCollapsed(nodes, nodes[node.parent]);
    }
  };

  getDisplaybleNodesArray = nodes => {
    let nodesArray = [];
    Object.keys(nodes).forEach(key => {
      // If "showHiddenNodes" is true, display all the nodes except the ones whoose any of the parent is collapsed.
      // Else, display the nodes which are not hidden and whoose any of the parent is not collapsed.
      if (this.props.showHiddenNodes) {
        if (!this.isAnyParentCollapsed(nodes, nodes[key])) {
          nodesArray.push(nodes[key]);
        }
      } else {
        if (
          !nodes[key][this.props.hiddenProp] &&
          !this.isAnyParentCollapsed(nodes, nodes[key])
        ) {
          nodesArray.push(nodes[key]);
        }
      }
    });
    return nodesArray;
  };

  updateNodeMetaData = nodesArray => {
    let updatedNodesArray = nodesArray.map(function (node, index) {
      node.index = index;
      return node;
    });
    return updatedNodesArray;
  };

  render() {
    let nodesArray = this.getDisplaybleNodesArray(this.nodes);
    nodesArray = this.updateNodeMetaData(nodesArray);
    const totalNodes = nodesArray.length,
      startNodeIndex = this.state.startNodeIndex,
      endNodeIndex = this.state.endNodeIndex,
      nodeHeight = this.props.nodeHeight;
    let topDivHeight = 0,
      bottomDivHeight = 0;
    if (totalNodes <= this.state.numberOfNodesToRender) {
      topDivHeight = 0;
      bottomDivHeight = 0;
    } else {
      topDivHeight = Math.max(startNodeIndex - 2, 0) * nodeHeight;
      bottomDivHeight =
        (totalNodes - startNodeIndex - this.state.numberOfNodesToRender) *
        nodeHeight;
      if (bottomDivHeight < 0) {
        bottomDivHeight = 0;
      }
    }
    const nodesToRender = this.getNodesToRender(
      Math.max(startNodeIndex - 2, 0),
      Math.min(endNodeIndex + 2, totalNodes),
      nodesArray
    );
    const treeNodes = this.renderTreeNodes(nodesToRender);
    const className = classNames(
      {
        "oui-tree": true,
        "oui-static-tree": !this.props.checkable,
        "oui-tree-disabled": this.props.disabled
      },
      this.props.className
    );
    return (
      <React.Fragment>
        {(this.props.heading ||
          (this.props.allowSelectAll && this.props.checkable)) && (
          <div
            className={gatherClasses(
              null,
              "oui-tree-heading",
              this.props.allowSelectAll && "oui-tree-select-all"
            )}
          >
            {this.props.allowSelectAll && (
              <Input
                innerRef={this.selectAllInputRef}
                type="checkbox"
                onChange={this.onSelectAll}
                checked={this.state.selectedAll}
                className="oui-d-inline-block"
              />
            )}
            {this.props.heading && <span>{this.props.heading}</span>}
          </div>
        )}
        <div
          data-testid={this.props.dataTestId}
          className="oui-tree-wrp"
          onScroll={this.onScroll}
          ref={this.treeContainer}
        >
          <div style={{ height: topDivHeight }} key="top" />
          <div className={className} role="tree">
            {treeNodes}
          </div>
          <div style={{ height: bottomDivHeight }} key="bottom" />
        </div>
      </React.Fragment>
    );
  }
}

Tree.propTypes = {
  nodes: PropTypes.arrayOf(NodeShape).isRequired,
  checked: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  expanded: PropTypes.arrayOf(PropTypes.string),
  loading: PropTypes.arrayOf(PropTypes.string),

  disabled: PropTypes.bool,
  checkable: PropTypes.bool,
  showNodeIcon: PropTypes.bool,
  expandDisabled: PropTypes.bool,
  showHiddenNodes: PropTypes.bool,

  uniqueDataKey: PropTypes.string,
  hiddenProp: PropTypes.string,
  disabledProp: PropTypes.string,
  labelProp: PropTypes.string,
  tooltipProp: PropTypes.string,
  checkboxTooltipProp: PropTypes.string,
  getCheckboxTooltip: PropTypes.func,
  checkableProp: PropTypes.string,
  childrenProp: PropTypes.string,
  childrenLevelMap: PropTypes.object,
  isStaticData: PropTypes.bool,

  nodeHeight: PropTypes.number,
  numberOfNodesToRender: PropTypes.number,

  onCheck: PropTypes.func,
  onExpand: PropTypes.func,

  renderIcon: PropTypes.func,
  renderLabel: PropTypes.func,
  renderSubLabel: PropTypes.func,
  renderNodeAction: PropTypes.func,

  heading: PropTypes.string,

  //unused props
  name: PropTypes.string,
  noCascade: PropTypes.bool,
  optimisticToggle: PropTypes.bool,
  returnOnlyLeafNodes: PropTypes.bool,
  dataTestId: PropTypes.string,
  allowSelectAll: PropTypes.bool
};

Tree.defaultProps = {
  nodes: [],
  checked: [],
  expanded: [],
  loading: [],

  disabled: false,
  checkable: true,
  showNodeIcon: true,
  expandDisabled: false,
  showHiddenNodes: false,

  uniqueDataKey: "value",
  hiddenProp: "hidden",
  disabledProp: "disabled",
  labelProp: "label",
  tooltipProp: "tooltipText",
  checkboxTooltipProp: "checkboxTooltipText",
  getCheckboxTooltip: null,
  checkableProp: "checkable",
  childrenProp: "children",
  childrenLevelMap: null,
  isStaticData: false,

  nodeHeight: 42,
  numberOfNodesToRender: 10,

  onCheck: () => {},
  onExpand: () => {},

  renderIcon: () => {
    return null;
  },
  renderSubLabel: () => {
    return null;
  },
  renderNodeAction: () => {
    return null;
  },

  heading: undefined,

  name: undefined,
  noCascade: false,
  optimisticToggle: false,
  returnOnlyLeafNodes: false,
  dataTestId: "orion-tree",
  allowSelectAll: false
};

Tree.displayName = "OrionTree";

export default Tree;
